import React from 'react';
import { LUT_CATEGORIES, LutParams } from './types';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/providers/i18n';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Upload as UploadIcon } from 'lucide-react';
import { useNodeId, useReactFlow } from '@xyflow/react';
import { nanoid } from 'nanoid';

interface LutPanelProps {
    params: LutParams;
    onChange: (params: Partial<LutParams>) => void;
}

export const LutPanel = ({ params, onChange }: LutPanelProps) => {
    const { t } = useI18n();
    const nodeId = useNodeId();
    const { addNodes, addEdges, getNode, setNodes } = useReactFlow();
    const hasImage =
        typeof (params as any)?.source_image_url === 'string' &&
        String((params as any).source_image_url).trim().length > 0;
    const uploadLabel = hasImage ? t('Click Run in toolbar') : t('Upload Image');

    // Find current category based on selected LUT
    const currentCategory = LUT_CATEGORIES.find(cat => cat.luts.some(l => l.id === params.lut_name)) || LUT_CATEGORIES[3]; // Default to Natural

    const handleAddUpload = () => {
        if (!nodeId) return;
        if (hasImage) return;
        try {
            const toolNode = getNode(nodeId) as any;
            if (!toolNode) return;
            const baseX = (toolNode?.position?.x ?? 0) as number;
            const baseY = (toolNode?.position?.y ?? 0) as number;
            const w = (toolNode?.measured?.width ?? 320) as number;
            const gap = 140;
            const newId = nanoid();

            addNodes({
                id: newId,
                type: 'upload',
                position: { x: baseX - w - gap, y: baseY },
                data: {
                    fileType: 'image',
                    updatedAt: new Date().toISOString(),
                },
                origin: [0, 0.5],
                selected: true,
            } as any);

            try {
                addEdges({
                    id: nanoid(),
                    source: newId,
                    target: nodeId,
                    type: 'animated',
                } as any);
            } catch { }

            // Select only the new upload node (so user can immediately upload)
            setTimeout(() => {
                try {
                    setNodes((prev: any[]) =>
                        prev.map((n: any) => ({ ...n, selected: String(n.id) === String(newId) }))
                    );
                } catch { }
            }, 0);
        } catch { }
    };

    return (
        <div className="flex flex-col gap-4 w-full h-full">
            <div className="flex items-center justify-between px-1">
                <div className="text-sm font-medium">{t('tool.lut.title')}</div>
            </div>

            <div className="space-y-4">
                {/* Source image: read from upstream edge (same behavior as Camera/Relight). */}
                <div className="space-y-2 nodrag">
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="w-full gap-2 border border-dashed border-primary/70 text-primary hover:bg-primary/10 hover:text-primary"
                        onClick={handleAddUpload}
                        disabled={!nodeId || hasImage}
                        title={uploadLabel}
                        aria-label={uploadLabel}
                    >
                        <UploadIcon size={14} />
                        {uploadLabel}
                    </Button>
                </div>

                <div className="space-y-2">
                    <Label>{t('tool.lut.category')}</Label>
                    <Select
                        value={currentCategory.id}
                        onValueChange={(catId) => {
                            const cat = LUT_CATEGORIES.find(c => c.id === catId);
                            if (cat && cat.luts.length > 0) {
                                onChange({ lut_name: cat.luts[0].id });
                            }
                        }}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {LUT_CATEGORIES.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>
                                    {t(`tool.lut.category.${cat.id}`) || cat.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label>{t('tool.lut.selection')}</Label>
                    <ScrollArea className="h-[160px] rounded-md p-2">
                        <div className="grid grid-cols-5 gap-1.5">
                            {currentCategory.luts.map((lut) => (
                                <div
                                    key={lut.id}
                                    className={cn(
                                        "flex flex-col items-center justify-center p-0.5 text-[9px] text-center rounded-md cursor-pointer transition-all border select-none aspect-square leading-[1.1] break-words overflow-hidden",
                                        params.lut_name === lut.id
                                            ? "bg-primary text-primary-foreground border-primary font-medium shadow-sm ring-1 ring-primary"
                                            : "bg-muted/30 hover:bg-muted/80 border-border/50 hover:border-border"
                                    )}
                                    onClick={() => onChange({ lut_name: lut.id })}
                                    title={lut.name}
                                >
                                    {lut.name}
                                </div>
                            ))}
                        </div>
                    </ScrollArea>
                </div>
            </div>
        </div>
    );
};
